import { useState } from 'react';

const PRIORITY_COLORS = {
  critical: 'badge-red',
  high: 'badge-yellow',
  medium: 'badge-blue',
  low: 'badge-green',
};

const PRIORITY_ICONS = {
  critical: '!!',
  high: '!',
  medium: 'i',
  low: '-',
};

function RecommendationCard({ rec }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card border border-surface-600 hover:border-surface-500 transition-colors">
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`${PRIORITY_COLORS[rec.priority]} text-xs`}>
              {rec.priority.toUpperCase()}
            </span>
            <h4 className="text-sm font-semibold text-gray-200 truncate">{rec.title}</h4>
          </div>
          {!expanded && (
            <p className="text-xs text-gray-500 line-clamp-2">{rec.rootCause}</p>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 flex-shrink-0 ml-2 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Root Cause */}
          <div>
            <h5 className="text-xs font-semibold text-red-400 uppercase tracking-wide mb-1">Root Cause</h5>
            <p className="text-sm text-gray-300 leading-relaxed">{rec.rootCause}</p>
          </div>

          {/* Recommendation */}
          <div>
            <h5 className="text-xs font-semibold text-brand-400 uppercase tracking-wide mb-1">Recommendation</h5>
            <p className="text-sm text-gray-300 leading-relaxed">{rec.recommendation}</p>
          </div>

          {/* Code Hints */}
          {rec.codeHints && rec.codeHints.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-emerald-400 uppercase tracking-wide mb-1">Code Suggestions</h5>
              <ul className="space-y-1.5">
                {rec.codeHints.map((hint, i) => (
                  <li key={i} className="flex items-start text-sm text-gray-400">
                    <span className="text-emerald-500 mr-2 mt-0.5 flex-shrink-0">&#8226;</span>
                    <code className="text-xs bg-surface-700 px-1.5 py-0.5 rounded text-gray-300">{hint}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Estimated Impact */}
          {rec.estimatedImpact && (
            <div className="bg-surface-700/50 rounded-lg p-3 border border-surface-600">
              <h5 className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-1">Estimated Impact</h5>
              <p className="text-sm text-gray-300">{rec.estimatedImpact}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function RecommendationPanel({ recommendations, title = 'Developer Recommendations' }) {
  if (!recommendations || recommendations.length === 0) {
    return (
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-2">{title}</h3>
        <div className="flex items-center gap-2 text-emerald-400">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">All metrics within healthy thresholds. No recommendations at this time.</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-300 mb-3">{title}</h3>
      <div className="space-y-3">
        {recommendations.map(rec => (
          <RecommendationCard key={rec.id} rec={rec} />
        ))}
      </div>
    </div>
  );
}
